// ============================================
// Huddle Indicator Component
// Shows active huddle in channel header
// ============================================

import React from 'react';

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Huddle Indicator - shows when a channel has an active video huddle
 */
const HuddleIndicator = ({ huddle, onJoin, isLoading }) => {
  if (!huddle?.active) return null;

  const participants = huddle.participants || [];
  const displayParticipants = participants.slice(0, 3);
  const remainingCount = participants.length - 3;

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-green-900/40 border-b border-green-700/50 animate-pulse-subtle">
      {/* Live indicator */}
      <div className="flex items-center space-x-1.5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
        </span>
        <span className="text-green-300 text-sm font-medium">Huddle</span>
      </div>

      {/* Participant avatars */}
      <div className="flex -space-x-2">
        {displayParticipants.map((p, i) => (
          <div
            key={`${p.user_type}-${p.user_id}`}
            className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 border-2 border-green-900 flex items-center justify-center text-white text-[10px] font-medium"
            title={p.display_name}
          >
            {getInitials(p.display_name)}
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="w-6 h-6 rounded-full bg-gray-600 border-2 border-green-900 flex items-center justify-center text-white text-[10px] font-medium">
            +{remainingCount}
          </div>
        )}
      </div>

      {/* Participant count */}
      <span className="text-green-300/80 text-sm">
        {huddle.participant_count} {huddle.participant_count === 1 ? 'person' : 'people'}
      </span>

      {/* Join button */}
      <button
        onClick={onJoin}
        disabled={isLoading}
        className="ml-auto px-3 py-1 text-sm bg-green-600 hover:bg-green-500 text-white rounded-full font-medium transition-colors disabled:opacity-50 flex items-center space-x-1"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
            <span>Joining...</span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>Join</span>
          </>
        )}
      </button>

      {/* Custom animation */}
      <style jsx>{`
        @keyframes pulse-subtle {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }
        .animate-pulse-subtle {
          animation: pulse-subtle 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

/**
 * Start Huddle Button - shown when no huddle is active
 */
export const StartHuddleButton = ({ onStart, isLoading }) => {
  return (
    <button
      onClick={onStart}
      disabled={isLoading}
      className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-green-400 hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50"
      title="Start a video huddle"
    >
      {isLoading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-400" />
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
      <span>Huddle</span>
    </button>
  );
};

export default HuddleIndicator;
